import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createNumberFilter } from '../../filters';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import type {
  TableColumnFormatLink,
  TableLinkColumnConfig,
} from '../../links/index';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';
import { mergeFieldDependencies } from './helpers/mergeFieldDependencies';

export type TableColumnFormatCompactNumber<TRow> = {
  type: 'compactNumber';
  field: KeyOfType<TRow, number | number[] | null>;
} & TableColumnFormatLink<TRow>;

type CompactNumberColumnConfig<TRow, TDataType> = TableDataColumnConfig<
  TRow,
  TDataType
> &
  TableLinkColumnConfig<TRow>;

const round = (number: number): number => {
  const factor = 10;
  const tempNumber = number * factor;
  const roundedTempNumber = Math.round(tempNumber);

  return roundedTempNumber / factor;
};

const makeFormatCompactNumberCellValue =
  (allowRounding: boolean) =>
  (value: number | null): string => {
    if (value === null) {
      return '';
    }

    const letter = (order: 0 | 1 | 2 | 3 | 4) => {
      switch (order) {
        case 0:
          return 'k';
        case 1:
          return 'M';
        case 2:
          return 'G';
        case 3:
          return 'T';
        case 4:
          return 'P';
        default:
          return '';
      }
    };

    const order = [0, 1, 2, 3, 4] as const;
    const readableValue = order.reduce(
      (acc, o) => {
        const { remaining } = acc;
        if (!allowRounding && remaining % 100 !== 0) {
          return acc;
        }
        if (remaining >= 100) {
          const newValue = round(remaining / 1000);
          const newLetter = letter(o);
          return { remaining: newValue, letter: newLetter };
        }
        return acc;
      },
      { remaining: Math.abs(value), letter: '' },
    );

    return `${value < 0 ? '-' : ''}${readableValue.remaining
      .toFixed(readableValue.letter === '' ? 0 : 1)
      .toLocaleString()}${readableValue.letter}`;
  };

export const formatCompactNumberRounded =
  makeFormatCompactNumberCellValue(true);
export const formatCompactNumberExact = makeFormatCompactNumberCellValue(false);

export const createCompactNumberColumn: {
  <TRow>(
    config: CompactNumberColumnConfig<TRow, number | null> & { array?: false },
  ): TableColumnBase<TRow, TableColumnFormatCompactNumber<TRow>>;
  <TRow>(
    config: CompactNumberColumnConfig<TRow, number[] | null> & { array: true },
  ): TableColumnBase<TRow, TableColumnFormatCompactNumber<TRow>>;
} = <TRow>(
  config: CompactNumberColumnConfig<TRow, number | number[] | null>,
): TableColumnBase<TRow, TableColumnFormatCompactNumber<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'compactNumber',
    field: config.key,
    createLinkProps: config.link?.createLinkProps,
  },
  csvStringDependencies: [config.key],
  webDependencies: mergeFieldDependencies(
    config.key,
    config.link?.dependencies,
  ),
  toCsvString: (row) =>
    formatValueToCsvString(
      getValueFromKeyOfType<TRow, number | number[] | null>(row, config.key),
      // intentionally not formatting with formatCompactNumberCellValue
      // to keep compatibility with old export. We can use that function
      // if we decide to keep export same as web display for this column type.
      (v: number | null): string => v?.toString(10) ?? '',
    ),
  filter: createDefaultFilter(
    config.filter,
    createNumberFilter({ compact: true }),
  ),
});
